import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/components/ui';

/**
 * 모달 껍데기.
 *
 * 왜 빼냈나 (2026-08-17)
 *   StockDetailModal 에만 있던 것이다. 관리자 회원 수정(3.7)이 두 번째로
 *   같은 것을 필요로 해서 공통으로 옮겼다. (CLAUDE.md 5.2절 — 2회 이상이면 추출)
 *
 *   두 벌로 두면 함정이 두 배가 된다. 아래 세 가지는 전부
 *   "빠뜨려도 평소에는 멀쩡해 보이는" 종류다.
 *
 *     배경 스크롤 잠금  폰에서 모달 안을 끝까지 스크롤하면 뒤 페이지가 밀려 올라간다
 *     ESC 로 닫기       키보드만 쓰는 사람에게는 유일한 탈출구다
 *     배경 클릭으로 닫기 안쪽 클릭이 새어 나가면 폼을 채우다 모달이 닫힌다
 *
 * 폰과 PC 에서 모양이 다르다
 *   폰   아래에서 올라오는 시트. 엄지가 닿는 자리에 붙는다
 *   PC   가운데 카드
 *   ConfirmProvider 와 같은 방식이라 앱 안에서 모달의 인상이 하나로 유지된다.
 */
export function Modal({
  open,
  onClose,
  children,
  /** 폰에서는 무시된다. 폰은 언제나 화면 폭을 다 쓴다 */
  maxWidth = 'sm:max-w-2xl',
  /** 제목 요소의 id. 스크린리더가 이 모달을 무엇이라고 읽을지 정한다 */
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  labelledBy?: string;
}) {
  /*
    배경 스크롤을 막는다.

    🔴 원래 값을 기억했다가 되돌린다
      빈 문자열로 되돌리면, 모달을 두 개 겹쳐 열었다가 하나만 닫았을 때
      바깥 모달이 아직 열려 있는데 배경이 다시 스크롤된다.
  */
  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  /*
    🔴 body 로 포털한다 (2026-09-28 사용자 피드백 — 스코어 도움말에 가로 스크롤이 생겨 글이 잘렸다)
      전엔 부른 자리(부모 DOM) 안에 그대로 그렸다. 그러면 부모의 상속 스타일을 물려받는다 —
      스코어 숫자를 감싼 span 의 `.tabular` 가 white-space: nowrap 이라(index.css) 모달 문단이
      줄바꿈되지 않고 한 줄로 뻗어 가로 스크롤이 생겼다. 표의 오른쪽 정렬 칸 · 굵은 글씨 · 오른쪽 정렬도
      같은 식으로 새어 들어올 수 있다. 부모와 DOM 을 끊으면 이런 누수가 구조적으로 없다.
      React 이벤트는 포털이어도 React 트리를 따라 올라가므로 동작(안쪽 클릭 stopPropagation)은 그대로다.
  */
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={onClose}
    >
      <div
        className={cx(
          'max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-surface shadow-xl sm:rounded-2xl',
          maxWidth,
        )}
        // 안쪽 클릭이 배경까지 올라가면 폼을 채우다 모달이 닫힌다.
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
